import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getEpicorOrder } from '../../services/epicorApi';
import PartCell from './PartCell';
import {
  datesSuspect,
  discountDisplay,
  fmt,
  fmtAmt,
  fmtDate,
  miscAmountDisplay,
  netExt,
  netUnitPrice,
  orderedDisplay,
} from './orderFormat';

const Corners = () => (
  <>
    <i className="oc-corner tl" aria-hidden="true" />
    <i className="oc-corner tr" aria-hidden="true" />
    <i className="oc-corner bl" aria-hidden="true" />
    <i className="oc-corner br" aria-hidden="true" />
  </>
);

const Stat = ({ value, label }) => (
  <div className="oc-stat oc-stat-static oc-blueprint">
    <Corners />
    <span className="oc-stat-value text">{value}</span>
    <span className="oc-stat-label">{label}</span>
  </div>
);

const OrderDetailPage = () => {
  const { company, orderNum } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('lines');

  const load = useCallback(async () => {
    try {
      setError(null);
      setData(await getEpicorOrder(company, orderNum));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [company, orderNum]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="oc-scope">
        <p className="oc-error">{error}</p>
        <Link to="/orders">← Back to orders</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="oc-scope"><p>Loading…</p></div>;
  }

  const {
    order, lines, shipments, shipment_lines, invoices, invoice_lines,
    order_misc: orderMisc = [], payments, totals,
  } = data;
  const suspect = datesSuspect(order);
  const tabs = [
    { id: 'lines', label: `Lines (${lines.length})` },
    { id: 'charges', label: `Charges (${orderMisc.length})` },
    { id: 'shipments', label: `Shipments (${shipments.length})` },
    { id: 'invoices', label: `Invoices (${invoices.length})` },
    { id: 'payments', label: `Payments (${payments.length})` },
  ];

  return (
    <div className="customers-page oc-scope">
      <header className="oc-header">
        <div>
          <Link to="/orders" className="oc-back">← Orders</Link>
          <h1 className="oc-title">
            {company} order {orderNum}
            {totals?.orphan_header ? (
              <span className="oc-hint"> (header not yet synced)</span>
            ) : null}
          </h1>
          <p className="oc-blurb">
            {fmtDate(order.order_date_effective || order.order_date)}
            {suspect && (
              <span className="oc-hint">
                {' '}· source date suspect (Epicor OrderDate {fmtDate(order.order_date)})
              </span>
            )}
            {order.cust_num != null && (
              <>
                {' · '}
                <Link to={`/customers/${company}/${order.cust_num}`}>
                  Customer #{order.cust_num}
                </Link>
              </>
            )}
            {order.po_num ? ` · PO ${order.po_num}` : ''}
            {order.currency_code ? ` · ${order.currency_code}` : ''}
          </p>
        </div>
      </header>

      <div className="oc-stats">
        <Stat value={fmtAmt(orderedDisplay({ ...order, ...totals }))} label="Ordered" />
        <Stat value={fmtAmt(totals.discount_amt)} label="Discount" />
        <Stat value={fmtAmt(totals.misc_amt)} label="Charges" />
        {/* Header-derived (charges - discount + misc), so it is right for the
            whole archive rather than only for re-synced orders. */}
        <Stat value={fmtAmt(totals.order_total_amt)} label="Order total" />
        <Stat value={fmtAmt(totals.shipped_amt)} label="Shipped" />
        <Stat value={fmtAmt(totals.invoiced_amt)} label="Invoiced" />
        <Stat value={fmtAmt(totals.paid_amt)} label="Paid" />
        <Stat value={fmtAmt(totals.outstanding_amt)} label="Outstanding" />
      </div>

      <div className="oc-frame oc-blueprint" style={{ marginTop: 24 }}>
        <Corners />
        <div className="oc-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={tab === t.id ? 'oc-tab active' : 'oc-tab'}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'lines' && (
          <table className="oc-table">
            <thead>
              <tr>
                <th>Line</th>
                <th>SKU</th>
                <th>Qty</th>
                <th>List</th>
                <th>Disc</th>
                <th>Net unit</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.order_line}>
                  <td>
                    {l.order_line}
                    {l.void_line ? (
                      <span className="oc-hint" style={{ marginLeft: 6 }}>Void</span>
                    ) : null}
                  </td>
                  <td>
                    <strong>
                      <PartCell partNum={l.part_num} pimSku={l.pim_sku} pimName={l.pim_name} />
                    </strong>
                    {l.line_desc ? <div className="oc-hint">{l.line_desc}</div> : null}
                  </td>
                  <td>{fmt(Number(l.selling_quantity ?? l.order_qty))}</td>
                  <td>{fmtAmt(l.doc_list_price ?? l.doc_unit_price)}</td>
                  <td>{discountDisplay(l)}</td>
                  <td>{fmtAmt(netUnitPrice(l))}</td>
                  <td>{fmtAmt(netExt(l))}</td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="oc-empty">
                      <p className="oc-empty-title">No line items staged for this order</p>
                      <p className="oc-empty-sub">Run a sales-orders sync to pull OrderDtl rows.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {tab === 'charges' && (
          <>
            <table className="oc-table">
              <thead>
                <tr>
                  <th>Applies to</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {orderMisc.map((m) => (
                  <tr key={`${m.order_line}-${m.seq_num}`}>
                    <td>{m.order_line ? `Line ${m.order_line}` : 'Order'}</td>
                    <td>{m.misc_code || '—'}</td>
                    <td>{m.description || '—'}</td>
                    <td>{miscAmountDisplay(m)}</td>
                  </tr>
                ))}
                {orderMisc.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <div className="oc-empty">
                        <p className="oc-empty-title">No freight or other charges on this order</p>
                        <p className="oc-empty-sub">
                          Charges are pulled by the sales-orders sync from Erp.OrderMsc.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {orderMisc.some((m) => m.misc_type === 'P') && (
              <p className="oc-hint" style={{ marginTop: 12 }}>
                Percentage charges show their rate: Epicor stores no per-row amount for
                them. They are still included in the Charges and Order total figures above,
                which come from the order header.
              </p>
            )}
          </>
        )}

        {tab === 'shipments' && (
          <>
            <table className="oc-table">
              <thead>
                <tr>
                  <th>Pack</th>
                  <th>Date</th>
                  <th>Ship via</th>
                  <th>Tracking</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map((s) => (
                  <tr key={s.pack_num}>
                    <td>{s.pack_num}</td>
                    <td>{fmtDate(s.ship_date)}</td>
                    <td>{s.ship_via_code || '—'}</td>
                    <td>{s.tracking_number || '—'}</td>
                  </tr>
                ))}
                {shipments.length === 0 && <tr><td colSpan={4}>No shipments.</td></tr>}
              </tbody>
            </table>
            {shipment_lines.length > 0 && (
              <table className="oc-table" style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>Pack</th>
                    <th>Line</th>
                    <th>Part</th>
                    <th>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {shipment_lines.map((l) => (
                    <tr key={`${l.pack_num}-${l.pack_line}`}>
                      <td>{l.pack_num}</td>
                      <td>{l.pack_line}</td>
                      <td><PartCell partNum={l.part_num} pimSku={l.pim_sku} pimName={l.pim_name} /></td>
                      <td>{fmt(Number(l.selling_inventory_ship_qty))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === 'invoices' && (
          <>
            <table className="oc-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Due</th>
                  <th>Amount</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.invoice_num}>
                    <td>
                      <Link to={`/invoices/${company}/${i.invoice_num}`}>{i.invoice_num}</Link>
                      {i.credit_memo ? <span className="oc-hint"> Credit note</span> : null}
                    </td>
                    <td>{fmtDate(i.invoice_date)}</td>
                    <td>{fmtDate(i.due_date)}</td>
                    <td>{fmtAmt(i.doc_invoice_amt)}</td>
                    <td>{fmtAmt(i.doc_invoice_bal)}</td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={5}>No invoices.</td></tr>}
              </tbody>
            </table>
            {invoice_lines.length > 0 && (
              <table className="oc-table" style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Line</th>
                    <th>SKU</th>
                    <th>Qty</th>
                    <th>List</th>
                    <th>Disc</th>
                    <th>Net unit</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice_lines.map((l) => (
                    <tr key={`${l.invoice_num}-${l.invoice_line}`}>
                      <td>
                        <Link to={`/invoices/${company}/${l.invoice_num}`}>{l.invoice_num}</Link>
                      </td>
                      <td>{l.invoice_line}</td>
                      <td><PartCell partNum={l.part_num} pimSku={l.pim_sku} pimName={l.pim_name} /></td>
                      <td>{fmt(Number(l.selling_ship_qty))}</td>
                      <td>{fmtAmt(l.doc_list_price ?? l.doc_unit_price)}</td>
                      <td>{discountDisplay(l)}</td>
                      <td>{fmtAmt(netUnitPrice(l))}</td>
                      <td>{fmtAmt(netExt(l))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === 'payments' && (
          <table className="oc-table">
            <thead>
              <tr>
                <th>Head</th>
                <th>Date</th>
                <th>Ref</th>
                <th>Amount</th>
                <th>Posted</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={`${p.group_id}-${p.head_num}`}>
                  <td>{p.group_id}/{p.head_num}</td>
                  <td>{fmtDate(p.tran_date)}</td>
                  <td>{p.check_ref || '—'}</td>
                  <td>{fmtAmt(p.doc_tran_amt)}</td>
                  <td>{p.posted ? 'Yes' : 'No'}</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={5}>No payments.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OrderDetailPage;
